/**
 * Palette colori centralizzata per i ruoli giocatori — Andrea 2026-05-22.
 *
 * UN UNICO posto per i colori, usato in:
 *   - Pitch / Bench / Pool (Schiera Formazione)
 *   - RosaPage (Gestione Rosa)
 *   - RosePage (vista tutte le rose)
 *   - GiocatoriPage (catalogo) + GiocatoreDetailPage (scheda singolo)
 *
 * Raggruppamenti Fantagazzetta Mantra (Andrea, agg. 2026-08-26: E → azzurro):
 *   Por                    → warning  (giallo) ★
 *   Dc/Dd/Ds/B             → success  (verde)  — difensori
 *   E/M/C                  → info     (azzurro) — centrocampisti + esterni
 *   W/T                    → purple   (viola)  — esterni offensivi + trequartisti
 *   A/Pc                   → danger   (rosso)  — attaccanti
 *
 * Tailwind JIT richiede che le classi siano stringhe statiche presenti
 * nel sorgente — niente template literals tipo `bg-${color}/20`. Per
 * questo ogni variante (full, soft, ring, gradient) e' definita
 * esplicitamente.
 */

export type ColorGroup = "Por" | "Def" | "Mid" | "Wing" | "Att";

/** Mappa ruolo Mantra label → gruppo colore. */
const MANTRA_TO_GROUP: Record<string, ColorGroup> = {
  Por: "Por",
  Dc: "Def",
  Dd: "Def",
  Ds: "Def",
  B: "Def",
  E: "Mid",
  M: "Mid",
  C: "Mid",
  W: "Wing",
  T: "Wing",
  A: "Att",
  Pc: "Att",
};

const CLASSIC_TO_GROUP: Record<string, ColorGroup> = {
  P: "Por",
  D: "Def",
  C: "Mid",
  A: "Att",
};

/** Classe "full" — sfondo pieno + testo contrastante. Usata nei pallini
 * ruolo (badge piccoli con lettera/i ruolo). */
export const FULL_BY_GROUP: Record<ColorGroup, string> = {
  Por: "bg-warning text-ink",
  Def: "bg-success text-ink",
  Mid: "bg-info text-ink",
  Wing: "bg-purple text-white",
  Att: "bg-danger text-white",
};

/** Classe "soft" — sfondo trasparente + bordo + testo colorato. Usata
 * nelle pill/chip delle PlayerCard. */
export const SOFT_BY_GROUP: Record<ColorGroup, string> = {
  Por: "bg-warning/20 text-warning border border-warning/30",
  Def: "bg-success/20 text-success border border-success/30",
  Mid: "bg-info/20 text-info border border-info/30",
  Wing: "bg-purple/20 text-purple border border-purple/30",
  Att: "bg-danger/20 text-danger border border-danger/30",
};

/** Determina il gruppo colore dato un giocatore (priorita' mantra,
 * fallback su classic). Ritorna `null` se nessuno disponibile. */
export function ruoloGroup(
  ruoliMantra: string[] | null | undefined,
  ruoloClassic: string | null | undefined,
): ColorGroup | null {
  if (ruoliMantra && ruoliMantra.length > 0) {
    const g = MANTRA_TO_GROUP[ruoliMantra[0]];
    if (g) return g;
  }
  if (ruoloClassic) {
    const c = ruoloClassic.charAt(0).toUpperCase();
    const g = CLASSIC_TO_GROUP[c];
    if (g) return g;
  }
  return null;
}

/** Classe colore "full" (sfondo pieno) per un giocatore. */
export function ruoloColor(
  ruoliMantra: string[] | null | undefined,
  ruoloClassic: string | null | undefined,
): string {
  const g = ruoloGroup(ruoliMantra, ruoloClassic);
  return g ? FULL_BY_GROUP[g] : "bg-fg-muted/40 text-fg";
}

/** Classe colore "soft" (chip/pill) per un giocatore. */
export function ruoloColorSoft(
  ruoliMantra: string[] | null | undefined,
  ruoloClassic: string | null | undefined,
): string {
  const g = ruoloGroup(ruoliMantra, ruoloClassic);
  return g
    ? SOFT_BY_GROUP[g]
    : "bg-fg-muted/20 text-fg-muted border border-fg-muted/30";
}

/** Per il PITCH (slot label diretta — non i ruoli del giocatore). */
export function ruoloColorFromSlotLabel(label: string | undefined): string {
  if (!label) return "bg-fg-muted/40 text-fg";
  const g = MANTRA_TO_GROUP[label];
  return g ? FULL_BY_GROUP[g] : "bg-fg-muted/40 text-fg";
}

/** Andrea 2026-05-23: per slot polivalenti tipo "W/A" o "C/T", trova
 * il ruolo "attivo" che il giocatore ricopre in quello slot.
 *
 * - Se la label dello slot e' un singolo ruolo (es. "C"), ritorna quello
 * - Se e' "X/Y" e il giocatore ha tra i suoi ruoli mantra "X" o "Y",
 *   ritorna il primo match
 * - Altrimenti ritorna il PRIMO termine della label (fallback safe)
 *
 * Esempi:
 *   resolveSlotRole("M/A", ["A","Pc"]) → "A"
 *   resolveSlotRole("C/T", ["C"]) → "C"
 *   resolveSlotRole("W/A", ["W"]) → "W"
 *   resolveSlotRole("C", ["C"]) → "C"
 *   resolveSlotRole("M/A", ["W"]) → "M" (fallback al primo)
 */
export function resolveSlotRole(
  slotLabel: string | undefined,
  ruoliMantraGiocatore: string[] | null | undefined,
): string {
  if (!slotLabel) return "";
  const parts = slotLabel.split("/").map((s) => s.trim()).filter(Boolean);
  if (parts.length <= 1) return slotLabel;
  if (ruoliMantraGiocatore && ruoliMantraGiocatore.length > 0) {
    for (const r of ruoliMantraGiocatore) {
      if (parts.includes(r)) return r;
    }
  }
  return parts[0];
}
